/**
 * Detail pages need a "Back" button that points at the page the user
 * actually came from (Contacts list, Pipelines, Inbox, …) rather than a
 * single hardcoded fallback. Callers pass `state: { from: pathname }`
 * when they navigate to the detail page; this helper reads that hint and
 * returns a target + a friendly label for the button.
 *
 * Falls back to the section's index page (`/contacts`) when no hint is
 * provided so the back button never breaks.
 */
export interface BackTarget {
  to: string;
  label: string;
}

const KNOWN: { match: (p: string) => boolean; to: string; label: string }[] = [
  { match: (p) => p === "/" || p.startsWith("/?"), to: "/", label: "Home" },
  { match: (p) => p === "/inbox" || p.startsWith("/inbox/"), to: "/inbox", label: "Inbox" },
  { match: (p) => p === "/mail" || p.startsWith("/mail/"), to: "/mail", label: "Mail" },
  { match: (p) => p === "/campaigns" || p.startsWith("/campaigns/"), to: "/campaigns", label: "Campaigns" },
  { match: (p) => p === "/pipelines" || p.startsWith("/pipelines/"), to: "/pipelines", label: "Pipelines" },
  { match: (p) => p === "/calendar" || p.startsWith("/calendar/"), to: "/calendar", label: "Calendar" },
  { match: (p) => p === "/tasks" || p.startsWith("/tasks/"), to: "/tasks", label: "Tasks" },
  { match: (p) => p === "/lists" || p.startsWith("/lists/"), to: "/lists", label: "Smart lists" },
  { match: (p) => p === "/automations" || p.startsWith("/automations/"), to: "/automations", label: "Automations" },
  { match: (p) => p.startsWith("/payments"), to: "/payments", label: "Payments" },
  { match: (p) => p.startsWith("/settings"), to: "/settings", label: "Settings" },
  { match: (p) => p === "/traffic" || p.startsWith("/traffic?"), to: "/traffic", label: "Traffic" },
  { match: (p) => p === "/contacts" || p.startsWith("/contacts?"), to: "/contacts", label: "Contacts" },
];

export function backFromState(state: unknown, fallback: BackTarget = { to: "/contacts", label: "Contacts" }): BackTarget {
  if (!state || typeof state !== "object") return fallback;
  const from = (state as { from?: unknown }).from;
  if (typeof from !== "string" || !from.startsWith("/")) return fallback;
  // Preserve query string in `to` so list filters etc. survive the round-trip.
  const path = from.split("?")[0]!;
  for (const k of KNOWN) {
    if (k.match(path)) return { to: from, label: k.label };
  }
  return fallback;
}
